import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Payroll ("ștat de plată") API client — Echipa: Angajare.
//
// Mirrors backend/src/routes/payroll.ts (preview/confirm/cancel) and the
// per-person history endpoint in backend/src/routes/people.ts.
// ---------------------------------------------------------------------------

export interface PayrollPreviewRow {
  rowId: number;
  extractedName: string;
  personId: string | null;
  matchedFullName: string | null;
  confidence: number;
  grossSalary: number;
  netSalary: number;
  employeeContributions: number;
  incomeTax: number;
  employerCam: number;
  totalCost: number;
}

export interface PayrollPreviewResult {
  documentId: string;
  year: number;
  month: number;
  rows: PayrollPreviewRow[];
  warnings: string[];
}

export interface ConfirmPayrollRow {
  personId: string;
  extractedName: string;
  grossSalary: number;
  netSalary: number;
  employeeContributions: number;
  incomeTax: number;
  employerCam: number;
  totalCost: number;
}

export interface ConfirmPayrollInput {
  year: number;
  month: number;
  rows: ConfirmPayrollRow[];
}

export interface PayrollEntry {
  id: string;
  documentId: string;
  documentFileName: string;
  year: number;
  month: number;
  extractedName: string;
  grossSalary: number;
  netSalary: number;
  employeeContributions: number;
  incomeTax: number;
  employerCam: number;
  totalCost: number;
  currency: string;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// POST /api/payroll/preview — raw-body PDF upload + AI extraction + matching.
export async function previewPayroll(
  file: File,
  opts: { year?: number; month?: number } = {},
): Promise<PayrollPreviewResult> {
  const query = new URLSearchParams({ name: file.name });
  if (opts.year) query.set('year', String(opts.year));
  if (opts.month) query.set('month', String(opts.month));
  const res = await apiFetch<Envelope<PayrollPreviewResult>>(
    `/api/payroll/preview?${query.toString()}`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': file.type || 'application/pdf',
      },
      body: file,
    },
  );
  return res.data;
}

// POST /api/payroll/:documentId/confirm — write the (corrected) entries.
export async function confirmPayroll(
  documentId: string,
  input: ConfirmPayrollInput,
): Promise<{ documentId: string; year: number; month: number; entriesWritten: number }> {
  const res = await apiFetch<
    Envelope<{ documentId: string; year: number; month: number; entriesWritten: number }>
  >(`/api/payroll/${documentId}/confirm`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

// DELETE /api/payroll/:documentId — cancel a preview / remove a document.
export async function cancelPayroll(documentId: string): Promise<{ deleted: boolean }> {
  const res = await apiFetch<Envelope<{ deleted: boolean }>>(`/api/payroll/${documentId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

// GET /api/payroll/:documentId/preview-url — signed URL for the original PDF.
export async function getPayrollDocumentUrl(
  documentId: string,
): Promise<{ url: string; mimeType: string | null; name: string }> {
  const res = await apiFetch<Envelope<{ url: string; mimeType: string | null; name: string }>>(
    `/api/payroll/${documentId}/preview-url`,
    { headers: authHeaders() },
  );
  return res.data;
}

// GET /api/people/:id/payroll — monthly history for a person.
export async function getPersonPayroll(personId: string): Promise<PayrollEntry[]> {
  const res = await apiFetch<Envelope<{ entries: PayrollEntry[] }>>(
    `/api/people/${personId}/payroll`,
    { headers: authHeaders() },
  );
  return res.data.entries;
}
